import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { onAuthStateChanged } from "firebase/auth";

import { useLocation, useNavigate } from "react-router-dom";

import {
  Send,
  Paperclip,
  Loader2,
  Plus,
  LogIn,
  Home,
  Info,
  X,
} from "lucide-react";

import { auth } from "../firebase";
import { easyChat } from "../easychat/engine";
import { trackClick } from "./trackClick";
import logo from "../New_Logo.png";

const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

const EASY_CAPABILITIES = [
  ["Make a worksheet", "Create a worksheet from a topic or saved chapters and download it as a branded PDF."],
  ["Start a quiz", "Answer 20 questions in chat, receive feedback, and save your score. Choose MCQ, Mental Maths, or Pattern Maths."],
  ["Explain or discuss a chapter", "Explore your selected chapter, ask questions, and practise with follow-up questions."],
  ["Help with a composition", "Get ideas, useful sentences, or a full composition in your chosen language, with a PDF download."],
  ["Create a comprehension passage", "Get a reading passage, questions, and a separate answer section, with a PDF download."],
  ["Check your answer", "Send a question and your answer for feedback and corrections. You can select a chapter for reference."],
  ["Add a lesson", "Save lesson text privately for worksheets, quizzes, and discussions in this chat. This does not publish it to the shared lesson library."],
  ["Show your performance", "Review your saved quiz and online-worksheet results from the last year, with class or school comparisons where data is available."],
  ["Find existing worksheets", "Find up to five uploaded worksheets matching your standard and requested subject, ranked by downloads."],
  ["Check your remaining generations", "See your remaining worksheet and quiz allowances for the rolling 30-day period, accounting for Premium membership."],
];

const ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
]);

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const value = String(reader.result || "");
      resolve(value.slice(value.indexOf(",") + 1));
    };

    reader.onerror = () => reject(
      new Error(`Could not read ${file.name}.`)
    );

    reader.readAsDataURL(file);
  });
}

function mergeMessages(previous, incoming) {
  const existing = new Set(previous.map(message => message.id));

  return [
    ...previous,
    ...incoming.filter(message => !existing.has(message.id)),
  ];
}

function isWorksheetDownload(download) {
  return Boolean(download && (
    download.kind === "worksheet" ||
    (!download.kind && /worksheet/i.test(download.name || ""))
  ));
}

function buildWorksheetPdfDocument(download) {
  const container = document.createElement("div");
  Object.assign(container.style, {
    width: "184.6mm",
    boxSizing: "border-box",
    background: "#ffffff",
    color: "#111827",
    fontFamily: 'Arial, "Nirmala UI", "Noto Sans Devanagari", sans-serif',
    overflowWrap: "anywhere",
  });

  function add(parent, tag, text, styles = {}) {
    const element = document.createElement(tag);
    element.textContent = text;
    Object.assign(element.style, styles);
    parent.appendChild(element);
    return element;
  }

  const cover = add(container, "section", "", {
    minHeight: "250mm",
    boxSizing: "border-box",
    padding: "12mm 8mm",
    textAlign: "center",
  });
  const image = document.createElement("img");
  image.src = logo;
  image.alt = "Eascode";
  Object.assign(image.style, {
    display: "block",
    width: "150px",
    height: "auto",
    margin: "0 auto 10px",
  });
  cover.appendChild(image);

  add(cover, "h1", "Eascode", {
    fontSize: "48px", fontWeight: "bold", margin: "10px 0",
  });
  add(cover, "p", "For Parents by Parents", {
    fontSize: "24px", fontWeight: "bold", margin: "10px 0 28px",
  });
  add(cover, "h2", download.name || "Worksheet", {
    fontSize: "24px", margin: "20px 0", lineHeight: "1.4",
  });

  const details = [
    download.standard && ["Standard", download.standard],
    download.school && ["School", download.school],
    download.lessonName && ["Chapters", download.lessonName],
    download.topics?.length && ["Topics", download.topics.join(", ")],
  ].filter(Boolean);
  details.forEach(([label, value]) => {
    add(cover, "p", `${label}: ${value}`, {
      fontSize: "14px", margin: "8px 0", lineHeight: "1.5",
    });
  });

  add(cover, "p", "For more worksheets based on your school syllabus and pattern visit:", {
    fontSize: "16px", fontWeight: "bold", margin: "28px 0 5px",
  });
  const link = add(cover, "a", "https://eascode.web.app/login", {
    display: "block", fontSize: "16px", color: "#0000ee",
    textDecoration: "underline",
  });
  link.href = "https://eascode.web.app/login";

  const content = add(container, "section", "", {
    padding: "6mm", boxSizing: "border-box",
    breakBefore: "page", pageBreakBefore: "always",
  });
  content.className = "easy-pdf-questions";

  String(download.content || "").split(/\r?\n/).forEach((line, index) => {
    const heading = (index === 0 && line.length < 150) ||
      /^(?:#{1,6}\s+|answers?\s*:?$|answer key\s*:?$|fill in the blanks\s*:?$|multiple choice questions\s*:?$|story sums\s*:?$|true or false\s*:?$|match the following\s*:?$)/i.test(line.trim());
    const text = line.replace(/^#{1,6}\s+/, "").replace(/^\*\*(.+)\*\*$/, "$1");
    add(content, heading ? "h2" : "div", text, {
      fontSize: heading ? "18px" : "16px",
      fontWeight: heading ? "bold" : "normal",
      lineHeight: "1.6",
      whiteSpace: "pre-wrap",
      margin: heading ? "14px 0 10px" : "0 0 6px",
      minHeight: line ? "" : "8px",
      breakInside: line.length < 1800 ? "avoid" : "auto",
      pageBreakInside: line.length < 1800 ? "avoid" : "auto",
    });
  });

  const terms = add(container, "section", "", {
    minHeight: "250mm", boxSizing: "border-box", padding: "12mm 10mm",
    breakBefore: "page", pageBreakBefore: "always",
  });
  terms.className = "easy-pdf-terms";
  add(terms, "h2", "Terms & Conditions", {
    fontSize: "20px", fontWeight: "bold", margin: "0 0 24px",
  });

  const paragraphs = [
    "This document is provided for personal use only. Unauthorized copying, " +
      "reproduction, distribution, or modification of any part of this content is strictly prohibited. " +
      "No portion of this material may be stored in a retrieval system, transmitted, or otherwise " +
      "used in any form or by any means—electronic, mechanical, photocopying, recording, or otherwise— " +
      "without the prior written permission of Eascode. All intellectual property rights, including " +
      "but not limited to copyrights, trademarks, and design rights, are fully reserved.",
    `© Eascode Pvt. Ltd. ${new Date().getFullYear()}.\nAll rights reserved. Unauthorized use of this document or its contents may result in ` +
      "civil and/or criminal liability under applicable laws.",
    "By using, viewing, or distributing this document, you expressly agree to comply with these terms.",
  ];
  paragraphs.forEach(text => add(terms, "p", text, {
    fontSize: "12px", color: "#444", lineHeight: "1.8",
    textAlign: "justify", whiteSpace: "pre-line", margin: "0 0 24px",
  }));

  return container;
}

function addWorksheetPdfWatermarks(pdf) {
  const totalPages = pdf.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    pdf.setPage(page);
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();

    pdf.saveGraphicsState();
    pdf.setGState(new pdf.GState({ opacity: 0.05 }));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(90);
    pdf.setTextColor(0, 0, 0);
    pdf.text("Eascode", width / 2, height / 2, { align: "center", angle: 30 });
    pdf.restoreGraphicsState();

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(110, 110, 110);
    pdf.text(`Eascode | ${page} / ${totalPages}`, width / 2, height - 7, { align: "center" });
  }
  pdf.setPage(1);
}

async function downloadPdf(download) {
  const module = await import("html2pdf.js");
  const html2pdf = module.default;
  const worksheet = isWorksheetDownload(download);
  const container = worksheet
    ? buildWorksheetPdfDocument(download)
    : document.createElement("div");

  if (!worksheet) {
    Object.assign(container.style, {
      width: "180mm", padding: "8mm", background: "#ffffff",
      color: "#111827",
      fontFamily: '"Nirmala UI", "Noto Sans Devanagari", Arial, sans-serif',
      fontSize: "12px", lineHeight: "1.6",
      whiteSpace: "pre-wrap", overflowWrap: "anywhere",
    });
    container.textContent = download.content;
  }

  const wrapper = document.createElement("div");
  wrapper.style.position = "absolute";
  wrapper.style.left = "-100000px";
  wrapper.style.top = "0";
  wrapper.setAttribute("aria-hidden", "true");
  wrapper.appendChild(container);
  document.body.appendChild(wrapper);

  try {
    if (document.fonts?.ready) await document.fonts.ready;
    await Promise.all(Array.from(container.querySelectorAll("img"), async image => {
      if (image.decode) {
        await image.decode().catch(() => {});
      } else if (!image.complete) {
        await new Promise(resolve => {
          image.onload = resolve;
          image.onerror = resolve;
        });
      }
    }));

    const worker = html2pdf()
      .set({
        margin: worksheet ? 12.7 : 10,
        filename: `${download.name || "Eascode"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, backgroundColor: "#ffffff", useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        enableLinks: true,
        pagebreak: {
          mode: ["css", "legacy"],
          ...(worksheet ? { before: [".easy-pdf-questions", ".easy-pdf-terms"] } : { avoid: ["p"] }),
        },
      })
      .from(container);

    if (worksheet) {
      const pdf = await worker.toPdf().get("pdf");
      pdf.setProperties({ title: download.name || "Eascode Worksheet", author: "Eascode" });
      addWorksheetPdfWatermarks(pdf);
    }
    await worker.save();
  } finally {
    wrapper.remove();
  }
}

export default function EasyUnifiedChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const submittedQuestionRef = useRef(null);
  const chatOpenedRef = useRef(false);
  const entrySourceRef = useRef(location.state?.easyQuestion ? "home_question_bar" : "direct");
  const analyticsSessionRef = useRef(null);
  const requestAnalyticsRef = useRef(null);

  const trackEasy = useCallback((event, details = {}) => {
    if (!analyticsSessionRef.current) analyticsSessionRef.current = crypto.randomUUID();
    // Analytics must never delay or interrupt the conversation.
    void trackClick("EasyUnifiedChat " + event, {
      feature: "EasyUnifiedChat",
      analyticsSessionId: analyticsSessionRef.current,
      entrySource: entrySourceRef.current,
      ...details,
    }).catch(() => {});
  }, []);

  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [threadId, setThreadId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsRetry, setNeedsRetry] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [showInfo, setShowInfo] = useState(false);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const infoButtonRef = useRef(null);
  const infoDialogRef = useRef(null);
  const infoCloseRef = useRef(null);
  const activeUidRef = useRef("");
  const busyRef = useRef(false);
  const pendingRef = useRef(null);

const callEasy = useRef(easyChat);

  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      activeUidRef.current = currentUser?.uid || "";

      if (!mounted) return;

      setUser(currentUser);
      setCheckingAuth(false);
      setMessages([]);
      setThreadId("");
      setInput("");
      setFiles([]);
      setError("");
      setNeedsRetry(false);

      pendingRef.current = null;
      requestAnalyticsRef.current = null;
      busyRef.current = false;
      setBusy(false);

      if (!currentUser) return;

      const uid = currentUser.uid;

      busyRef.current = true;
      setBusy(true);

      try {
        const result = await callEasy.current({
          action: "bootstrap",
        });

        if (!mounted || activeUidRef.current !== uid) return;

        setThreadId(result.data.threadId);
        setMessages(result.data.messages || []);
      } catch (err) {
        if (mounted && activeUidRef.current === uid) {
          setError(
            err.message || "Could not load your conversation."
          );
          trackEasy("Request Failed", {
            operation: "bootstrap",
            userId: uid,
            errorCode: String(err.code || "unknown"),
          });
        }
      } finally {
        if (mounted && activeUidRef.current === uid) {
          busyRef.current = false;
          setBusy(false);
        }
      }
    });

    return () => {
      mounted = false;
      activeUidRef.current = "";
      unsubscribe();
    };
  }, [trackEasy]);

  useEffect(() => {
    if (checkingAuth || chatOpenedRef.current) return;
    chatOpenedRef.current = true;
    trackEasy("Chat Opened", { signedIn: Boolean(user) });
  }, [checkingAuth, user, trackEasy]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, busy]);

  useEffect(() => {
    if (!showInfo) return undefined;

    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    infoCloseRef.current?.focus();

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowInfo(false);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = infoDialogRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], [tabindex="0"]'
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [showInfo]);

  function login() {
    sessionStorage.setItem(
      "eascode_post_login_redirect",
      "/EasyChat"
    );

    sessionStorage.removeItem("eascode_auth_redirect_started");
    sessionStorage.removeItem("eascode_auth_redirect_retry");

    navigate("/auth-loading");
  }

  async function pickFiles(event) {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";

    if (!selected.length) return;

    if (selected.length > MAX_ATTACHMENTS) {
      setError("Attach no more than four files at once.");
      return;
    }

    if (selected.some(file => !ALLOWED_TYPES.has(file.type))) {
      setError("Use JPG, PNG, WebP, or PDF files.");
      return;
    }

    const total = selected.reduce(
      (sum, file) => sum + file.size,
      0
    );

    if (total > MAX_ATTACHMENT_BYTES) {
      setError("Attachments must total no more than 5 MB.");
      return;
    }

    setError("");
    setFiles(selected);
  }

  const send = useCallback(async (initialMessage) => {
    if (!user || busyRef.current || !threadId) return;

    const text = (initialMessage?.text ?? input).trim();

    if (!pendingRef.current && !text && !files.length) {
      return;
    }

    const uid = user.uid;
    const requestId = pendingRef.current?.requestId || initialMessage?.requestId || crypto.randomUUID();
    const attemptStartedAt = Date.now();
    let analytics = requestAnalyticsRef.current;
    const firstAttempt = !analytics || analytics.requestId !== requestId;
    if (firstAttempt) {
      analytics = {
        requestId,
        startedAt: attemptStartedAt,
        attempt: 0,
        entrySource: initialMessage?.requestId ? "home_question_bar" : "chat_composer",
      };
      requestAnalyticsRef.current = analytics;
    }
    analytics.attempt += 1;
    const requestDetails = {
      operation: "turn",
      userId: uid,
      threadId,
      requestId,
      attempt: analytics.attempt,
      entrySource: analytics.entrySource,
    };

    busyRef.current = true;
    setBusy(true);
    setError("");

    if (firstAttempt) {
      trackEasy("Question Submitted", {
        ...requestDetails,
        questionLength: text.length,
        attachmentCount: files.length,
        attachmentTypes: [...new Set(files.map(file => file.type))],
      });
    }

    try {
      if (!pendingRef.current) {
        const attachments = await Promise.all(
          files.map(async file => ({
            name: file.name,
            mimeType: file.type,
            data: await readBase64(file),
          }))
        );

        if (activeUidRef.current !== uid) return;

        pendingRef.current = {
          action: "turn",
          requestId,
          threadId,
          text,
          attachments,
        };
      }

      const pending = pendingRef.current;

      const result = await callEasy.current(pending);

      if (activeUidRef.current !== uid) return;

      trackEasy("Response Received", {
        ...requestDetails,
        responseThreadId: result.data.threadId,
        durationMs: Date.now() - attemptStartedAt,
        totalDurationMs: Date.now() - analytics.startedAt,
        assistantMessageCount: (result.data.messages || []).filter(message => message.role === "assistant").length,
      });

      setMessages(previous =>
        result.data.append
          ? mergeMessages(previous, result.data.messages || [])
          : result.data.messages || []
      );

      setThreadId(result.data.threadId);
      setInput("");
      setFiles([]);
      setNeedsRetry(false);
      pendingRef.current = null;
      requestAnalyticsRef.current = null;
    } catch (err) {
      if (activeUidRef.current === uid) {
        trackEasy("Request Failed", {
          ...requestDetails,
          durationMs: Date.now() - attemptStartedAt,
          errorCode: String(err.code || "unknown"),
          retryable: Boolean(pendingRef.current),
        });
        setError(
          err.message ||
          "The request could not finish. Retry the same message."
        );

        // Keep the same request ID for network retries.
        setNeedsRetry(Boolean(pendingRef.current));
      }
    } finally {
      if (activeUidRef.current === uid) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }, [user, threadId, input, files, trackEasy]);

  useEffect(() => {
    const question = location.state?.easyQuestion;
    if (
      checkingAuth || !user || !threadId || busy || busyRef.current || needsRetry ||
      typeof question?.text !== "string" || !question.text.trim() ||
      typeof question.requestId !== "string" || !question.requestId ||
      submittedQuestionRef.current === question.requestId
    ) return;

    submittedQuestionRef.current = question.requestId;
    setInput(question.text);

    // Consume the handoff so refresh or Back cannot submit it again.
    const remainingState = { ...location.state };
    delete remainingState.easyQuestion;
    navigate(location.pathname + location.search + location.hash, {
      replace: true,
      state: remainingState,
    });

    send(question);
  }, [checkingAuth, user, threadId, busy, needsRetry, location, navigate, send]);

  async function newConversation() {
    if (!user || busyRef.current) return;

    const uid = user.uid;

    busyRef.current = true;
    setBusy(true);
    setError("");

    try {
      const result = await callEasy.current({
        action: "new",
      });

      if (activeUidRef.current !== uid) return;

      setThreadId(result.data.threadId);
      setMessages(result.data.messages || []);
      setInput("");
      setFiles([]);
      setNeedsRetry(false);
      pendingRef.current = null;
      requestAnalyticsRef.current = null;
    } catch (err) {
      if (activeUidRef.current === uid) {
        trackEasy("Request Failed", {
          operation: "new_conversation",
          userId: uid,
          threadId,
          errorCode: String(err.code || "unknown"),
        });
        setError(
          err.message || "Could not create a conversation."
        );
      }
    } finally {
      if (activeUidRef.current === uid) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }

  async function handleDownload(event, item) {
    event.preventDefault();

    if (downloading) return;

    setDownloading(item.id);
    setError("");

    try {
      await downloadPdf(item.download);
      trackEasy("PDF Downloaded", {
        operation: "pdf_download",
        userId: user?.uid || null,
        threadId,
        messageId: item.id,
        downloadKind: isWorksheetDownload(item.download) ? "worksheet" : (item.download.kind || "document"),
      });
    } catch (err) {
      trackEasy("Request Failed", {
        operation: "pdf_download",
        userId: user?.uid || null,
        threadId,
        messageId: item.id,
        errorCode: String(err.code || "unknown"),
      });
      setError(
        err.message || "Could not generate the PDF."
      );
    } finally {
      setDownloading("");
    }
  }

  return (
    <div className="flex h-[100dvh] flex-col bg-slate-50 text-slate-900">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-black text-indigo-700">
            Easy
          </h1>

          <p className="truncate text-[10px] text-slate-500 sm:text-xs">
            Your Eascode study conversation
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {user && (
            <button
              type="button"
              onClick={newConversation}
              disabled={busy}
              className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 disabled:opacity-40"
            >
              <Plus size={16} />
              New chat
            </button>
          )}

          {!checkingAuth && (
            user ? (
              <button
                type="button"
                onClick={() => navigate("/Old_Home")}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-50 px-2 py-2 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 sm:px-3 sm:text-sm"
                aria-label="Back to home"
                title="Back to home"
              >
                <Home size={18} />
                Back to home
              </button>
            ) : (
              <button
                type="button"
                onClick={login}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white"
              >
                <LogIn size={16} />
                Sign in
              </button>
            )
          )}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-6">
        <div className="mx-auto max-w-3xl space-y-5">
          {!user && !checkingAuth && (
            <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700">
              Hi, I’m Easy. Sign in and tell me what you want
              to work on. I can create worksheets, run quizzes,
              explain chapters, and help with writing—all
              through this conversation.
            </p>
          )}

          {messages.map(item => (
            <div
              key={item.id}
              className={
                item.role === "user"
                  ? "ml-auto max-w-[88%] rounded-2xl bg-indigo-600 px-4 py-3 text-white"
                  : "max-w-full py-2"
              }
            >
              {item.role === "assistant" && (
                <p className="mb-2 text-xs font-black text-indigo-700">
                  Easy
                </p>
              )}

              <div className="whitespace-pre-wrap break-words text-sm leading-7">
                {isWorksheetDownload(item.download)
                  ? "Here's your worksheet. Happy learning!"
                  : item.text}
              </div>

              {item.download && (
                <a
                  href="#download"
                  onClick={event => handleDownload(event, item)}
                  className="mt-3 inline-block text-sm font-semibold text-indigo-700 underline underline-offset-4"
                >
                  {downloading === item.id
                    ? "Preparing PDF…"
                    : isWorksheetDownload(item.download) ? "Download worksheet PDF" : "Download PDF"}
                </a>
              )}

              {item.links?.map((link, index) => (
                <p key={`${item.id}-link-${index}`} className="mt-2">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-700 underline underline-offset-4"
                  >
                    {link.label}
                  </a>
                </p>
              ))}
            </div>
          ))}

          {busy && (
            <p
              className="flex items-center gap-2 text-sm text-slate-500"
              role="status"
            >
              <Loader2 size={16} className="animate-spin" />
              Easy is thinking…
            </p>
          )}

          <div ref={bottomRef} />
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white px-4 py-3">
        <div className="mx-auto max-w-3xl">
          {error && (
            <p
              className="mb-2 whitespace-pre-wrap text-sm text-red-600"
              role="alert"
            >
              {error}

              {!threadId && user && (
                <button
                  type="button"
                  onClick={newConversation}
                  className="ml-2 underline"
                  disabled={busy}
                >
                  Start a conversation
                </button>
              )}
            </p>
          )}

          {files.length > 0 && (
            <p className="mb-2 text-xs text-slate-500">
              Attached: {files.map(file => file.name).join(", ")}

              {!needsRetry && !busy && (
                <button
                  type="button"
                  onClick={() => setFiles([])}
                  className="ml-2 underline"
                >
                  Remove
                </button>
              )}
            </p>
          )}

          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              multiple
              onChange={pickFiles}
              className="hidden"
            />

            <div className="flex w-10 shrink-0 flex-col items-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!user || busy || needsRetry}
                className="flex h-11 w-10 items-center justify-center text-slate-500 disabled:opacity-40"
                title="Attach chapter images or PDF"
                aria-label="Attach files"
              >
                <Paperclip size={20} />
              </button>

              <button
                ref={infoButtonRef}
                type="button"
                onClick={() => setShowInfo(true)}
                className="flex h-11 w-10 items-center justify-center rounded-lg text-indigo-600 transition hover:bg-indigo-50"
                title="What Easy can do"
                aria-label="What Easy can do"
                aria-haspopup="dialog"
                aria-expanded={showInfo}
                aria-controls="easy-capabilities-dialog"
              >
                <Info size={20} />
              </button>
            </div>

            <textarea
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
              rows={2}
              maxLength={120000}
              disabled={!user || busy || needsRetry}
              placeholder={
                user
                  ? "Tell Easy what you want to do Like make me a worksheet or a quiz…"
                  : "Sign in to start…"
              }
              className="max-h-48 min-h-12 min-w-0 flex-1 resize-y rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 disabled:bg-slate-50"
              aria-label="Message Easy"
            />

            <button
              type="button"
              onClick={() => send()}
              disabled={
                !user ||
                !threadId ||
                busy ||
                (!needsRetry && !input.trim() && !files.length)
              }
              className="flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-600 px-3 text-sm font-bold text-white disabled:opacity-40"
              title={needsRetry ? "Retry message" : "Send message"}
              aria-label={needsRetry ? "Retry message" : "Send message"}
            >
              {needsRetry ? "Retry" : <Send size={18} />}
            </button>
          </div>

          <p className="mt-2 text-center text-[10px] text-slate-400">
            Type /cancel to stop a task or /allowance to check usage.
          </p>
        </div>
      </footer>

      {showInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowInfo(false)}
            className="absolute inset-0 bg-slate-950/50"
            aria-label="Close information popup"
          />
          <section
            ref={infoDialogRef}
            id="easy-capabilities-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="easy-capabilities-title"
            aria-describedby="easy-capabilities-description"
            className="relative max-h-[85dvh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 id="easy-capabilities-title" className="text-lg font-bold text-slate-900">
                What Easy can help you with
              </h2>
              <button
                ref={infoCloseRef}
                type="button"
                onClick={() => setShowInfo(false)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100"
                aria-label="Close information"
              >
                <X size={20} />
              </button>
            </div>
            <p id="easy-capabilities-description" className="mt-2 text-sm leading-6 text-slate-600">
              Just tell Easy what you would like to work on. Features use your saved profile, available lessons, and activity records.
            </p>
            <section aria-labelledby="easy-attachments-title" className="mt-5 border-b border-slate-200 pb-5">
              <h3 id="easy-attachments-title" className="text-sm font-bold text-indigo-700">
                Using attachments
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Attach JPG, PNG, or WebP images, or PDF files. You can select up to {MAX_ATTACHMENTS} files totalling {MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB.
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Easy extracts their text so you can:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600">
                <li>Create worksheets or quizzes from chapter pages.</li>
                <li>Explain or discuss the attached chapter.</li>
                <li>Save the extracted text as a private lesson.</li>
                <li>Check an answer sheet when Easy asks for your question and answer.</li>
              </ul>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Attachments are used for text extraction; diagrams may not be interpreted reliably. Word files, spreadsheets, audio, and video are not supported. Attaching a file does not publish it to the shared worksheet library.
              </p>
            </section>
            <dl className="mt-5 space-y-4">
              {EASY_CAPABILITIES.map(([title, description]) => (
                <div key={title}>
                  <dt className="text-sm font-bold text-indigo-700">{title}</dt>
                  <dd className="mt-1 text-sm leading-6 text-slate-600">{description}</dd>
                </div>
              ))}
            </dl>
            <button
              type="button"
              onClick={() => setShowInfo(false)}
              className="mt-6 w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              Close
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
